import { useLayoutEffect, useRef, useState } from 'react';
import { ImperativePanelHandle } from 'react-resizable-panels';

/**
 * Collapse-sync for the left-stack cards (Customer Pulse / Live Transcript /
 * Action Center). Each card's chevron drives its open flag; each vertical
 * panel's own collapse/expand callbacks keep the flag in sync when resized by
 * drag. Returns the open flags + setters (for the cards) and the panel refs
 * (for the Panels).
 *
 * useLayoutEffect (not useEffect): apply the panel collapse/expand in the SAME
 * paint as the card's open/closed render. With useEffect the card body
 * unmounted in one frame and the panel resized in the next — a visible
 * two-phase that read as a laggy accordion.
 */
const useLeftStackCollapse = () => {
  const [cpOpen, setCpOpen] = useState(true);
  const [acOpen, setAcOpen] = useState(true);
  const [ltOpen, setLtOpen] = useState(true);
  const cpPanelRef = useRef<ImperativePanelHandle>(null);
  const acPanelRef = useRef<ImperativePanelHandle>(null);
  const ltPanelRef = useRef<ImperativePanelHandle>(null);

  useLayoutEffect(() => {
    const panel = cpPanelRef.current;
    if (!panel) return;
    if (cpOpen && panel.isCollapsed()) panel.expand();
    else if (!cpOpen && panel.isExpanded()) panel.collapse();
  }, [cpOpen]);
  useLayoutEffect(() => {
    const panel = acPanelRef.current;
    if (!panel) return;
    if (acOpen && panel.isCollapsed()) panel.expand();
    else if (!acOpen && panel.isExpanded()) panel.collapse();
  }, [acOpen]);
  useLayoutEffect(() => {
    const panel = ltPanelRef.current;
    if (!panel) return;
    if (ltOpen && panel.isCollapsed()) panel.expand();
    else if (!ltOpen && panel.isExpanded()) panel.collapse();
  }, [ltOpen]);

  return {
    cpOpen,
    setCpOpen,
    acOpen,
    setAcOpen,
    ltOpen,
    setLtOpen,
    cpPanelRef,
    acPanelRef,
    ltPanelRef,
  };
};

export default useLeftStackCollapse;
